<template>
  <AppLayout
    customToolbar
    :isLoading="isFetchingOfferGroupDocument"
    centered
    class="offer-document"
  >
    <template v-slot:additional-title>
      <span class="secondary-text ml-2"> {{ role }} </span>
    </template>
    <template v-slot:body>
      <div class="container col-lg-10 col-sm-12">
        <div v-if="isFetchingOfferGroupDocument || isFetchingCurrentOffer">
          <v-skeleton-loader v-for="i in 5" :key="i" type="list-item" />
        </div>
        <div v-else-if="offerGroupDocument">
          <BackBar :parent="parentPath" :title="title">
            <template v-slot:additional-title>
              <span class="secondary-text ml-2"> {{ role }} </span>
            </template>
          </BackBar>
          <div
            class="d-flex justify-center align-center mb-5"
            :class="{ 'flex-column': isMobile }"
          >
            <h2>
              {{ offerGroupDocument.name }}
            </h2>
            <Chip
              v-if="!isInformation"
              :color="getColorByStatus(offerGroupDocument.status)"
              class="chip--status mt-1"
              shrink
              :class="{ 'ml-3': !isMobile }"
            >
              {{ offerGroupDocument.status | upperCase }}
            </Chip>
          </div>
          <Permissioned vertical section="offers.document">
            <Comment
              permission="comments"
              :isAdding="isAddingComment"
              :isLoading="isFetchingOfferGroupDocumentComments"
              :comments="offerGroupDocumentComments"
              @comment="createNewComment"
              :minimizable="false"
              smallTitle
            />
          </Permissioned>
          <div v-if="size(offerGroupDocument.rejectedDocuments)">
            <p class="font-weight-bold">Rejected Documents</p>
            <FileComponent
              class="mb-5"
              v-for="file in offerGroupDocument.rejectedDocuments"
              :key="file.id"
              :file="file"
              @show="showFile(file)"
              customActions
              disabled
              :showFileName="!isMobile"
            />
          </div>
          <div v-if="canFillDocument">
            <FillableDocument
              :document="offerGroupDocument.sourceDocument"
              v-if="isWorkerProfile"
              @onFileChanged="file => (uploadedFile = file)"
            />
            <NotUploadedDocument
              :document="offerGroupDocument.sourceDocument"
              v-else
            />
          </div>
          <InformationDocument
            v-else
            :document="
              offerGroupDocument.responseDocument ||
                offerGroupDocument.sourceDocument
            "
          />
          <PDFViewFull :data="fileDialog" />
        </div>
      </div>
      <BaseFooter>
        <ExpandedButtons>
          <Permissioned
            :vertical="isMobile"
            section="offers.document"
            class="actions-bar"
          >
            <RejectDocument permission="reject" />
            <ApproveDocument permission="approve" />
            <SubmitForApprovalAction
              v-if="showSubmitForApprovalButton"
              :file="uploadedFile"
              permission="submit"
            />
          </Permissioned>
        </ExpandedButtons>
      </BaseFooter>
    </template>
  </AppLayout>
</template>
<script>
import AppLayout from "@/components/common/base-layouts/AppLayout";
import {
  FETCH_OFFER_GROUP_DOCUMENT,
  FETCH_CURRENT_OFFER,
  ADD_OFFER_DOCUMENT_COMMENT,
  FETCH_OFFER_DOCUMENT_COMMENTS
} from "@/store/modules/offers/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { createNamespacedHelpers } from "vuex";
import { getColorByStatus } from "@/utils/offers";
import Chip from "@/components/common/Chip";
import { getFullName } from "@/utils/users";
import { isEmpty, size, get } from "lodash";
import BackBar from "@/components/common/base-layouts/BackBar";
import FillableDocument from "@/views/offers/components/OfferDocument/components/FillableDocument";
import NotUploadedDocument from "@/views/offers/components/OfferDocument/components/NotUploadedDocument";
import InformationDocument from "@/components/documents/InformationDocument";
import ApproveDocument from "@/views/offers/components/OfferDocument/actions/ApproveDocumentAction";
import RejectDocument from "@/views/offers/components/OfferDocument/actions/RejectDocumentAction";
import SubmitForApprovalAction from "@/views/offers/components/OfferDocument/actions/SubmitForApprovalAction";
import BaseFooter from "@/components/common/BaseFooter";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import Permissioned from "@/components/common/Permissioned";
import { isPermissioned } from "@/utils/permissions";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { isWorker } from "@/utils/users";
import Comment from "@/components/common/Comment";
import PDFViewFull from "@/components/common/PDFViewFull";
import FileComponent from "@/components/common/FileComponent";
import { getFilePreviewSettings } from "@/utils/files";

const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);
const {
  mapActions: mapOfferActions,
  mapState: mapOfferState
} = createNamespacedHelpers(OFFERS_NAMESPACE);

export default {
  components: {
    AppLayout,
    Chip,
    BackBar,
    FillableDocument,
    NotUploadedDocument,
    InformationDocument,
    ApproveDocument,
    RejectDocument,
    BaseFooter,
    ExpandedButtons,
    Permissioned,
    SubmitForApprovalAction,
    Comment,
    PDFViewFull,
    FileComponent
  },
  data() {
    return {
      uploadedFile: null,
      fileDialog: { dialog: false },
      isAddingComment: false
    };
  },
  async created() {
    if (isEmpty(this.currentOffer)) {
      await this.fetchCurrentOffer(this.currentOfferId);
    }
    await this.fetchOfferGroupDocument({
      offerId: this.currentOfferId,
      documentId: this.offerGroupDocumentId
    });
    if (isPermissioned("offers.document.comments")) {
      this.fetchComments({
        offerId: this.currentOfferId,
        documentId: this.offerGroupDocumentId
      });
    }
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData,
      profiles: state => state.profiles
    }),
    ...mapOfferState([
      "currentOffer",
      "isFetchingCurrentOffer",
      "isFetchingOfferGroupDocument",
      "offerGroupDocument",
      "offerGroupDocumentComments",
      "isFetchingOfferGroupDocumentComments"
    ]),
    isWorkerProfile() {
      return isWorker(this.self.accountType);
    },
    parentPath() {
      return `/offers/${this.currentOfferId}`;
    },
    canFillDocument() {
      return (
        this.offerGroupDocument.documentType === "fillable" &&
        isPermissioned("offers.document.fill")
      );
    },
    application() {
      return get(this.currentOffer, "application") || {};
    },
    role() {
      return this.application.booking && this.application.booking.title;
    },
    title() {
      return this.application ? getFullName(this.application.user) : "";
    },
    candidate() {
      return this.application.user;
    },
    offerGroupDocumentId() {
      return this.$route.params.documentId;
    },
    currentOfferId() {
      return this.$route.params.id;
    },
    showSubmitForApprovalButton() {
      return this.uploadedFile;
    },
    isInformation() {
      return this.offerGroupDocument.documentType === "information";
    }
  },
  methods: {
    ...mapOfferActions({
      fetchOfferGroupDocument: FETCH_OFFER_GROUP_DOCUMENT,
      fetchCurrentOffer: FETCH_CURRENT_OFFER,
      addComment: ADD_OFFER_DOCUMENT_COMMENT,
      fetchComments: FETCH_OFFER_DOCUMENT_COMMENTS
    }),
    async showFile(file) {
      const filePreviewSettings = await getFilePreviewSettings(file);
      this.fileDialog = {
        ...filePreviewSettings,
        dialog: true
      };
    },
    async createNewComment(comment) {
      this.isAddingComment = true;
      await this.addComment({
        body: comment,
        commentableType: "offer_document",
        commentableId: this.offerGroupDocumentId
      });
      this.isAddingComment = false;
    },
    getColorByStatus,
    size
  }
};
</script>

<style lang="scss"></style>
